import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { ZSwitch } from 'src/components/widgets/zswitch';
import { T, backofficeUrl, toLocalID } from 'src/shared/helpers';
import type { PricingRuleEditorProps, PricingGroupData, PricingRuleData, PricingConditionData } from '../common/types';
import type { pricingRuleEditor_PricingDetailQuery } from './__generated__/pricingRuleEditor_PricingDetailQuery.graphql';
import type { pricingRuleEditor_UpdatePricingMutation } from './__generated__/pricingRuleEditor_UpdatePricingMutation.graphql';
import { useConditionOperators, parseFormula, buildFormula } from '../common/constants';
import {
    StackedBreadcrumb,
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    FullModeLink,
    FullModeHint,
    FullModeIframeContainer,
    FullModeSkeleton,
    SectionCard,
    SectionCardTitle,
    FormLabel,
    FormSelect,
    SettingHint,
    SharedWarningCallout,
    CompactSwitchGrid,
    CompactSwitchRow,
    CompactSwitchLabel,
    PricingRuleCard,
    PricingRuleCardHeader,
    PricingRuleCardBody,
    PriceInput,
    DeleteRuleButton,
    QuantityTierContainer,
    QuantityTierRow,
    FormGridRow,
    FormSelectCompact,
    FormLabelCompact,
    FormInputCompact,
    FormMarginTop,
    ConditionFormRow,
    ConditionLabel,
    CurrencyLabel,
    AddQuantityButton,
    AddTextButton,
    AddRuleButton,
    RulePropertyName,
    RuleElementTitle,
    RuleMetaLine,
    RuleHeaderActions,
    ExpandArrow,
    QuantityTierHeader,
    QuantityTierLabel,
    EmptyConditions,
    EmptyRulesCenter,
    EmptyRulesSmall,
    TargetsSection,
    TargetsInfo,
    IframeEmbed,
    FixedWidthSpacer
} from '../common/styled';

const pricingDetailQuery = graphql`
    query pricingRuleEditor_PricingDetailQuery($id: ID!) {
        pricing(id: $id) {
            id
            pricingID
            pricingName
            infoPricingText
            priceDescriptionEnabled
            quantityAsUnit
            priceStructure
            products {
                productID
            }
            rules {
                ruleID
                propertyID
                elementID
                elementTitle
                isSetupPrice
                allMatchType
                priceType
                simplified
                conditions {
                    conditionID
                    formula
                    position
                    priceToAdd
                    quantity
                }
                targets {
                    targetID
                    area
                    color
                    side
                }
            }
        }
        pricingProperties {
            propertyID
            propertyName
            elements {
                elementID
                elementName
            }
        }
    }
`;

const productNameQuery = graphql`
    query pricingRuleEditor_ProductNameQuery($id: ID!) {
        node(id: $id) {
            ... on Model {
                name
            }
        }
    }
`;

const PricingRuleEditorContent: FC<PricingRuleEditorProps> = ({ pricingId, productId, onBack, onGoToFullTool }) => {
    const CONDITION_OPERATORS = useConditionOperators();
    const [saving, setSaving] = useState(false);
    const [fullMode, setFullMode] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());
    const [dirty, setDirty] = useState(false);

    const json = useLazyLoadQuery<pricingRuleEditor_PricingDetailQuery>(pricingDetailQuery, { id: pricingId });
    const productData = useLazyLoadQuery<any>(productNameQuery, { id: productId });
    const productName = (productData?.node as any)?.name || '';
    const assignedProductCount = json?.pricing?.products?.length;

    const [properties] = useState<
        ReadonlyArray<{ propertyID: number; propertyName: string; elements: ReadonlyArray<{ elementID: number; elementName: string }> }>
    >(() => json?.pricingProperties || []);
    const [pricingGroup, setPricingGroup] = useState<PricingGroupData | null>(() => {
        const pg = json?.pricing;
        const props = json?.pricingProperties || [];
        if (!pg) return null;
        return {
            id: pg.id,
            pricingName: pg.pricingName,
            infoPricingText: pg.infoPricingText || '',
            priceDescriptionEnabled: pg.priceDescriptionEnabled ?? false,
            quantityAsUnit: pg.quantityAsUnit ?? false,
            priceStructure: pg.priceStructure || 'FINAL_PRICE',
            rules: (pg.rules || []).map((rule: any) => ({
                ...rule,
                simplified: rule.simplified ?? false,
                propertyName: props.find((pricing: any) => pricing.propertyID === rule.propertyID)?.propertyName || `Property #${rule.propertyID}`,
                conditions: (rule.conditions || []).map((condition: any) => ({
                    ...condition,
                    priceToAdd: String(condition.priceToAdd ?? '0'),
                    quantity: String(condition.quantity ?? '0')
                }))
            }))
        };
    });

    const pricingName = pricingGroup?.pricingName || '';

    const [commitUpdatePricing] = useMutation<pricingRuleEditor_UpdatePricingMutation>(graphql`
        mutation pricingRuleEditor_UpdatePricingMutation($input: PricingGroupInput!) {
            updatePricing(input: $input) {
                pricingGroup {
                    id
                }
            }
        }
    `);

    const toggleRule = (ruleID: number) => {
        setExpandedRules((prev) => {
            const next = new Set(prev);
            next.has(ruleID) ? next.delete(ruleID) : next.add(ruleID);
            return next;
        });
    };

    const updatePricingField = <K extends keyof PricingGroupData>(field: K, value: PricingGroupData[K]) => {
        if (!pricingGroup) return;
        setPricingGroup({ ...pricingGroup, [field]: value });
        setDirty(true);
    };

    const updateRuleField = <K extends keyof PricingRuleData>(ruleID: number, field: K, value: PricingRuleData[K]) => {
        if (!pricingGroup) return;
        setPricingGroup({
            ...pricingGroup,
            rules: pricingGroup.rules.map((rule) => (rule.ruleID === ruleID ? { ...rule, [field]: value } : rule))
        });
        setDirty(true);
    };

    const updateConditionField = (ruleID: number, conditionID: number, field: keyof PricingConditionData, value: string) => {
        if (!pricingGroup) return;
        setPricingGroup({
            ...pricingGroup,
            rules: pricingGroup.rules.map((rule) =>
                rule.ruleID === ruleID
                    ? {
                          ...rule,
                          conditions: rule.conditions.map((condition) => (condition.conditionID === conditionID ? { ...condition, [field]: value } : condition))
                      }
                    : rule
            )
        });
        setDirty(true);
    };

    const deleteRule = (ruleID: number) => {
        if (!pricingGroup) return;
        setPricingGroup({ ...pricingGroup, rules: pricingGroup.rules.filter((rule) => rule.ruleID !== ruleID) });
        setDirty(true);
    };

    const handleSave = () => {
        if (!pricingGroup) return;
        setSaving(true);
        const input = {
            id: pricingGroup.id,
            pricingName: { value: pricingGroup.pricingName },
            infoPricingText: { value: pricingGroup.infoPricingText },
            priceDescriptionEnabled: { value: pricingGroup.priceDescriptionEnabled },
            quantityAsUnit: { value: pricingGroup.quantityAsUnit },
            priceStructure: { value: pricingGroup.priceStructure },
            rules: {
                value: pricingGroup.rules.map((rule) => ({
                    ruleID: rule.ruleID,
                    propertyID: { value: rule.propertyID },
                    elementID: { value: rule.elementID },
                    elementTitle: { value: rule.elementTitle },
                    isSetupPrice: { value: rule.isSetupPrice },
                    allMatchType: { value: rule.allMatchType },
                    priceType: { value: rule.priceType },
                    conditions: {
                        value: rule.conditions.map((condition) => ({
                            conditionID: condition.conditionID,
                            priceToAdd: { value: parseFloat(condition.priceToAdd) || 0 },
                            position: { value: condition.position },
                            formula: { value: condition.formula },
                            quantity: { value: parseInt(condition.quantity) || 0 }
                        }))
                    }
                }))
            }
        };

        commitUpdatePricing({
            variables: { input: input as any },
            onCompleted: () => {
                setDirty(false);
                setSaving(false);
            },
            onError: () => {
                setSaving(false);
            }
        });
    };

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: pricingName, subtitle: fullMode ? T._('Pricing rules — Full mode', 'Admin') : T._('Pricing rules', 'Admin') }}
            width={fullMode ? window.innerWidth - 80 : 620}
            footer={
                <>
                    {!fullMode && <FullModeHint>{T._('+ more settings available in full mode', 'Admin')}</FullModeHint>}
                    <FullModeLink
                        onClick={() => {
                            setFullMode(!fullMode);
                            if (!fullMode) setIframeLoading(true);
                        }}
                    >
                        {fullMode ? '↙ ' + T._('Compact mode', 'Admin') : '↗ ' + T._('Full mode', 'Admin')}
                    </FullModeLink>
                    {!fullMode && (
                        <StackedFooterButtons>
                            <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                            <FooterButton $primary disabled={!dirty || saving} onClick={handleSave}>
                                {saving ? T._('Saving...', 'Admin') : T._('Save Changes', 'Admin')}
                            </FooterButton>
                        </StackedFooterButtons>
                    )}
                </>
            }
        >
            {fullMode ? (
                <FullModeIframeContainer>
                    {iframeLoading && (
                        <FullModeSkeleton>
                            <ContentLoader
                                speed={2}
                                width='100%'
                                height='516'
                                backgroundColor={theme.skeleton.backgroundColor}
                                foregroundColor={theme.skeleton.foregroundColor}
                            >
                                <rect x='0' y='0' rx='8' ry='8' width='30%' height='28' />
                                <rect x='0' y='52' rx='8' ry='8' width='100%' height='16' />
                                <rect x='0' y='76' rx='8' ry='8' width='80%' height='16' />
                                <rect x='0' y='116' rx='8' ry='8' width='60%' height='40' />
                                <rect x='0' y='180' rx='8' ry='8' width='100%' height='16' />
                                <rect x='0' y='204' rx='8' ry='8' width='90%' height='16' />
                                <rect x='0' y='244' rx='8' ry='8' width='45%' height='40' />
                                <rect x='0' y='316' rx='8' ry='8' width='100%' height='200' />
                            </ContentLoader>
                        </FullModeSkeleton>
                    )}
                    <IframeEmbed
                        src={backofficeUrl('/pricing-advanced-embedded/?pricing_id=' + toLocalID(pricingId)) + '&embedded=true'}
                        $loading={iframeLoading}
                        title={pricingName}
                        onLoad={() => setIframeLoading(false)}
                    />
                </FullModeIframeContainer>
            ) : (
                <>
                    <StackedBreadcrumb>
                        <span onClick={onBack}>{productName}</span> / {T._('Pricing rules', 'Admin')} / {pricingName}
                    </StackedBreadcrumb>

                    <StackedBody>
                        {assignedProductCount != null && assignedProductCount > 1 && (
                            <SharedWarningCallout>
                                {T._('This pricing rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                                {T._('products. Any changes will apply to all of them.', 'Admin')}
                            </SharedWarningCallout>
                        )}
                        {!pricingGroup ? (
                            <EmptyRulesCenter>{T._('No rules configured', 'Admin')}</EmptyRulesCenter>
                        ) : (
                            <>
                                {/* ---- DISPLAY OPTIONS ---- */}
                                <SectionCard>
                                    <SectionCardTitle>{T._('Display options', 'Admin')}</SectionCardTitle>

                                    <FormLabelCompact>{T._('Price structure', 'Admin')}</FormLabelCompact>
                                    <FormSelectCompact
                                        value={pricingGroup.priceStructure}
                                        onChange={(element) => updatePricingField('priceStructure', element.target.value)}
                                    >
                                        <option value='FINAL_PRICE'>{T._('Show final price', 'Admin')}</option>
                                        <option value='SEPARATED_PRICES'>{T._('Separate product + customization', 'Admin')}</option>
                                        <option value='CUSTOMIZATION_PRICE'>{T._('Only customization charge', 'Admin')}</option>
                                    </FormSelectCompact>

                                    <CompactSwitchGrid>
                                        <CompactSwitchRow>
                                            <CompactSwitchLabel>{T._('Unit price', 'Admin')}</CompactSwitchLabel>
                                            <ZSwitch
                                                checked={pricingGroup.quantityAsUnit}
                                                onChange={() => updatePricingField('quantityAsUnit', !pricingGroup.quantityAsUnit)}
                                            />
                                        </CompactSwitchRow>
                                        <CompactSwitchRow>
                                            <CompactSwitchLabel>{T._('Price description', 'Admin')}</CompactSwitchLabel>
                                            <ZSwitch
                                                checked={pricingGroup.priceDescriptionEnabled}
                                                onChange={() => updatePricingField('priceDescriptionEnabled', !pricingGroup.priceDescriptionEnabled)}
                                            />
                                        </CompactSwitchRow>
                                    </CompactSwitchGrid>

                                    {pricingGroup.priceDescriptionEnabled && (
                                        <FormMarginTop>
                                            <FormInputCompact
                                                value={pricingGroup.infoPricingText}
                                                onChange={(element) => updatePricingField('infoPricingText', element.target.value)}
                                                placeholder={T._('Hover text for price info', 'Admin')}
                                            />
                                            <SettingHint>{T._('Rich text editor in full mode', 'Admin')}</SettingHint>
                                        </FormMarginTop>
                                    )}
                                </SectionCard>

                                {/* ---- RULES ---- */}
                                <SectionCard>
                                    <SectionCardTitle>{T._('Rules', 'Admin')}</SectionCardTitle>

                                    {pricingGroup.rules.length === 0 ? (
                                        <EmptyRulesSmall>{T._('No rules configured', 'Admin')}</EmptyRulesSmall>
                                    ) : (
                                        pricingGroup.rules.map((rule) => {
                                            const isExpanded = expandedRules.has(rule.ruleID);
                                            return (
                                                <PricingRuleCard key={rule.ruleID}>
                                                    <PricingRuleCardHeader onClick={() => toggleRule(rule.ruleID)}>
                                                        <div>
                                                            <RulePropertyName>{rule.propertyName}</RulePropertyName>
                                                            {rule.elementTitle && <RuleElementTitle>{rule.elementTitle}</RuleElementTitle>}
                                                            <RuleMetaLine>
                                                                {rule.conditions.length}{' '}
                                                                {rule.conditions.length === 1 ? T._('condition', 'Admin') : T._('conditions', 'Admin')}
                                                                {' · '}
                                                                {rule.isSetupPrice ? T._('Setup', 'Admin') : T._('Design', 'Admin')}
                                                                {' · '}
                                                                {rule.priceType === 1 ? '%' : T._('Fixed', 'Admin')}
                                                                {' · '}
                                                                {rule.allMatchType ? T._('All match', 'Admin') : T._('First match', 'Admin')}
                                                            </RuleMetaLine>
                                                        </div>
                                                        <RuleHeaderActions>
                                                            <DeleteRuleButton
                                                                onClick={(element) => {
                                                                    element.stopPropagation();
                                                                    deleteRule(rule.ruleID);
                                                                }}
                                                                title={T._('Delete rule', 'Admin')}
                                                            >
                                                                &#10005;
                                                            </DeleteRuleButton>
                                                            <ExpandArrow>{isExpanded ? '▴' : '▾'}</ExpandArrow>
                                                        </RuleHeaderActions>
                                                    </PricingRuleCardHeader>
                                                    {isExpanded && (
                                                        <PricingRuleCardBody>
                                                            {/* Rule settings */}
                                                            <FormGridRow $columns={2}>
                                                                <div>
                                                                    <FormLabel>{T._('Property', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.propertyID}
                                                                        onChange={(element) => {
                                                                            const newPropId = Number(element.target.value);
                                                                            const newPropName =
                                                                                properties.find((pricing) => pricing.propertyID === newPropId)?.propertyName ||
                                                                                `Property #${newPropId}`;
                                                                            if (!pricingGroup) return;
                                                                            setPricingGroup({
                                                                                ...pricingGroup,
                                                                                rules: pricingGroup.rules.map((r) =>
                                                                                    r.ruleID === rule.ruleID
                                                                                        ? {
                                                                                              ...r,
                                                                                              propertyID: newPropId,
                                                                                              propertyName: newPropName,
                                                                                              elementID: 0,
                                                                                              elementTitle: null
                                                                                          }
                                                                                        : r
                                                                                )
                                                                            });
                                                                            setDirty(true);
                                                                        }}
                                                                    >
                                                                        {properties.map((pricing) => (
                                                                            <option key={pricing.propertyID} value={pricing.propertyID}>
                                                                                {pricing.propertyName}
                                                                            </option>
                                                                        ))}
                                                                    </FormSelectCompact>
                                                                </div>
                                                                <div>
                                                                    <FormLabel>{T._('Element', 'Admin')}</FormLabel>
                                                                    {(() => {
                                                                        const prop = properties.find((pricing) => pricing.propertyID === rule.propertyID);
                                                                        const elements = prop?.elements || [];
                                                                        return (
                                                                            <FormSelectCompact
                                                                                value={rule.elementID || 0}
                                                                                onChange={(element) => {
                                                                                    const elId = Number(element.target.value);
                                                                                    const elName =
                                                                                        elements.find((el) => el.elementID === elId)?.elementName || null;
                                                                                    updateRuleField(rule.ruleID, 'elementID', elId);
                                                                                    updateRuleField(rule.ruleID, 'elementTitle', elName);
                                                                                }}
                                                                            >
                                                                                <option value={0}>{T._('All', 'Admin')}</option>
                                                                                {elements.map((el) => (
                                                                                    <option key={el.elementID} value={el.elementID}>
                                                                                        {el.elementName}
                                                                                    </option>
                                                                                ))}
                                                                            </FormSelectCompact>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </FormGridRow>

                                                            <FormGridRow $columns={3}>
                                                                <div>
                                                                    <FormLabel>{T._('Type', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.isSetupPrice ? 'setup' : 'design'}
                                                                        onChange={(element) =>
                                                                            updateRuleField(rule.ruleID, 'isSetupPrice', element.target.value === 'setup')
                                                                        }
                                                                    >
                                                                        <option value='setup'>{T._('Setup', 'Admin')}</option>
                                                                        <option value='design'>{T._('Design', 'Admin')}</option>
                                                                    </FormSelectCompact>
                                                                </div>
                                                                <div>
                                                                    <FormLabel>{T._('Match', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.allMatchType ? 'all' : 'first'}
                                                                        onChange={(element) =>
                                                                            updateRuleField(rule.ruleID, 'allMatchType', element.target.value === 'all')
                                                                        }
                                                                    >
                                                                        <option value='all'>{T._('All', 'Admin')}</option>
                                                                        <option value='first'>{T._('First match', 'Admin')}</option>
                                                                    </FormSelectCompact>
                                                                </div>
                                                                <div>
                                                                    <FormLabel>{T._('Price type', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.priceType}
                                                                        onChange={(element) =>
                                                                            updateRuleField(rule.ruleID, 'priceType', Number(element.target.value))
                                                                        }
                                                                    >
                                                                        <option value={0}>{T._('Fixed', 'Admin')}</option>
                                                                        <option value={1}>{T._('Percentage', 'Admin')}</option>
                                                                    </FormSelectCompact>
                                                                </div>
                                                            </FormGridRow>

                                                            {/* Conditions */}
                                                            <FormLabelCompact>{T._('Conditions', 'Admin')}</FormLabelCompact>
                                                            {rule.conditions.length === 0 && <EmptyConditions>{T._('No conditions', 'Admin')}</EmptyConditions>}
                                                            {(() => {
                                                                // Group conditions by formula for quantity tiers
                                                                const groups: { formula: string; conditions: PricingConditionData[] }[] = [];
                                                                rule.conditions.forEach((cond) => {
                                                                    const existing = groups.find((g) => g.formula === cond.formula);
                                                                    if (existing) {
                                                                        existing.conditions.push(cond);
                                                                    } else {
                                                                        groups.push({
                                                                            formula: cond.formula,
                                                                            conditions: [cond]
                                                                        });
                                                                    }
                                                                });

                                                                return groups.map((group, groupIndex) => {
                                                                    const parsed = parseFormula(group.formula);
                                                                    const hasQuantityTiers = !(
                                                                        group.conditions.length === 1 && Number(group.conditions[0].quantity) === 0
                                                                    );
                                                                    const connector =
                                                                        CONDITION_OPERATORS.find((op) => op.value === parsed.operator)?.connector || 'to';
                                                                    return (
                                                                        <div key={groupIndex} style={{ marginBottom: 6 }}>
                                                                            {/* Condition row: If [operator] [connector] [value] then apply [price] */}
                                                                            <ConditionFormRow>
                                                                                <ConditionLabel $minWidth={10}>If</ConditionLabel>
                                                                                <FormSelect
                                                                                    value={parsed.operator}
                                                                                    onChange={(element) => {
                                                                                        const newFormula = buildFormula(element.target.value, parsed.values);
                                                                                        const updatedConditions = rule.conditions.map((condition) =>
                                                                                            condition.formula === group.formula
                                                                                                ? {
                                                                                                      ...condition,
                                                                                                      formula: newFormula
                                                                                                  }
                                                                                                : condition
                                                                                        );
                                                                                        updateRuleField(rule.ruleID, 'conditions', updatedConditions);
                                                                                    }}
                                                                                    style={{
                                                                                        fontSize: 11,
                                                                                        padding: '3px 4px',
                                                                                        width: 95,
                                                                                        minWidth: 95
                                                                                    }}
                                                                                >
                                                                                    {CONDITION_OPERATORS.map((op) => (
                                                                                        <option key={op.value} value={op.value}>
                                                                                            {op.label}
                                                                                        </option>
                                                                                    ))}
                                                                                </FormSelect>
                                                                                <ConditionLabel $minWidth={26}>{connector}</ConditionLabel>
                                                                                <PriceInput
                                                                                    type='number'
                                                                                    placeholder={T._('Val', 'Admin')}
                                                                                    value={parsed.values[0] || ''}
                                                                                    onChange={(element) => {
                                                                                        const newFormula = buildFormula(parsed.operator, [
                                                                                            element.target.value
                                                                                        ]);
                                                                                        const updatedConditions = rule.conditions.map((condition) =>
                                                                                            condition.formula === group.formula
                                                                                                ? {
                                                                                                      ...condition,
                                                                                                      formula: newFormula
                                                                                                  }
                                                                                                : condition
                                                                                        );
                                                                                        updateRuleField(rule.ruleID, 'conditions', updatedConditions);
                                                                                    }}
                                                                                    style={{ width: 50 }}
                                                                                />
                                                                                {!hasQuantityTiers && (
                                                                                    <>
                                                                                        <ConditionLabel $minWidth={30}>{T._('apply', 'Admin')}</ConditionLabel>
                                                                                        <PriceInput
                                                                                            type='number'
                                                                                            step='0.01'
                                                                                            value={group.conditions[0].priceToAdd}
                                                                                            onChange={(element) =>
                                                                                                updateConditionField(
                                                                                                    rule.ruleID,
                                                                                                    group.conditions[0].conditionID,
                                                                                                    'priceToAdd',
                                                                                                    element.target.value
                                                                                                )
                                                                                            }
                                                                                            style={{ width: 55 }}
                                                                                            placeholder={T._('Price', 'Admin')}
                                                                                        />
                                                                                        <CurrencyLabel>{rule.priceType === 1 ? '%' : '€'}</CurrencyLabel>
                                                                                    </>
                                                                                )}
                                                                                {!hasQuantityTiers && (
                                                                                    <AddQuantityButton
                                                                                        type='button'
                                                                                        onClick={() => {
                                                                                            const newTier: PricingConditionData = {
                                                                                                conditionID: Date.now(),
                                                                                                formula: group.formula,
                                                                                                position: group.conditions[0].position,
                                                                                                priceToAdd: '0',
                                                                                                quantity: '1'
                                                                                            };
                                                                                            const updated = rule.conditions.map((condition) =>
                                                                                                condition.conditionID === group.conditions[0].conditionID
                                                                                                    ? {
                                                                                                          ...condition,
                                                                                                          quantity: '1'
                                                                                                      }
                                                                                                    : condition
                                                                                            );
                                                                                            updateRuleField(rule.ruleID, 'conditions', [...updated, newTier]);
                                                                                        }}
                                                                                        title={T._('Add quantity tiers', 'Admin')}
                                                                                    >
                                                                                        +qty
                                                                                    </AddQuantityButton>
                                                                                )}
                                                                                <DeleteRuleButton
                                                                                    onClick={() => {
                                                                                        updateRuleField(
                                                                                            rule.ruleID,
                                                                                            'conditions',
                                                                                            rule.conditions.filter(
                                                                                                (condition) => condition.formula !== group.formula
                                                                                            )
                                                                                        );
                                                                                    }}
                                                                                    title={T._('Delete condition', 'Admin')}
                                                                                >
                                                                                    &#10005;
                                                                                </DeleteRuleButton>
                                                                            </ConditionFormRow>

                                                                            {/* Quantity tiers — only shown when multiple qty rows */}
                                                                            {hasQuantityTiers && (
                                                                                <QuantityTierContainer>
                                                                                    <QuantityTierHeader>
                                                                                        <QuantityTierLabel>{T._('Qty (from)', 'Admin')}</QuantityTierLabel>
                                                                                        <QuantityTierLabel $width={70}>
                                                                                            {T._('Price', 'Admin')}
                                                                                        </QuantityTierLabel>
                                                                                        <FixedWidthSpacer />
                                                                                    </QuantityTierHeader>
                                                                                    {group.conditions
                                                                                        .sort((a, b) => Number(a.quantity) - Number(b.quantity))
                                                                                        .map((tier) => (
                                                                                            <QuantityTierRow key={tier.conditionID}>
                                                                                                <PriceInput
                                                                                                    type='number'
                                                                                                    value={tier.quantity}
                                                                                                    onChange={(element) =>
                                                                                                        updateConditionField(
                                                                                                            rule.ruleID,
                                                                                                            tier.conditionID,
                                                                                                            'quantity',
                                                                                                            element.target.value
                                                                                                        )
                                                                                                    }
                                                                                                    style={{
                                                                                                        flex: 1,
                                                                                                        width: 'auto'
                                                                                                    }}
                                                                                                    placeholder='0'
                                                                                                />
                                                                                                <PriceInput
                                                                                                    type='number'
                                                                                                    step='0.01'
                                                                                                    value={tier.priceToAdd}
                                                                                                    onChange={(element) =>
                                                                                                        updateConditionField(
                                                                                                            rule.ruleID,
                                                                                                            tier.conditionID,
                                                                                                            'priceToAdd',
                                                                                                            element.target.value
                                                                                                        )
                                                                                                    }
                                                                                                    style={{ width: 70 }}
                                                                                                />
                                                                                                <DeleteRuleButton
                                                                                                    onClick={() => {
                                                                                                        const remaining = group.conditions.filter(
                                                                                                            (condition) =>
                                                                                                                condition.conditionID !== tier.conditionID
                                                                                                        );
                                                                                                        if (remaining.length === 1) {
                                                                                                            const updated = rule.conditions
                                                                                                                .filter(
                                                                                                                    (condition) =>
                                                                                                                        condition.conditionID !==
                                                                                                                        tier.conditionID
                                                                                                                )
                                                                                                                .map((condition) =>
                                                                                                                    condition.conditionID ===
                                                                                                                    remaining[0].conditionID
                                                                                                                        ? {
                                                                                                                              ...condition,
                                                                                                                              quantity: '0'
                                                                                                                          }
                                                                                                                        : condition
                                                                                                                );
                                                                                                            updateRuleField(rule.ruleID, 'conditions', updated);
                                                                                                        } else {
                                                                                                            updateRuleField(
                                                                                                                rule.ruleID,
                                                                                                                'conditions',
                                                                                                                rule.conditions.filter(
                                                                                                                    (condition) =>
                                                                                                                        condition.conditionID !==
                                                                                                                        tier.conditionID
                                                                                                                )
                                                                                                            );
                                                                                                        }
                                                                                                    }}
                                                                                                >
                                                                                                    &#10005;
                                                                                                </DeleteRuleButton>
                                                                                            </QuantityTierRow>
                                                                                        ))}
                                                                                    <AddTextButton
                                                                                        type='button'
                                                                                        onClick={() => {
                                                                                            const maxQty = Math.max(
                                                                                                ...group.conditions.map(
                                                                                                    (condition) => Number(condition.quantity) || 0
                                                                                                )
                                                                                            );
                                                                                            const newTier: PricingConditionData = {
                                                                                                conditionID: Date.now() + Math.random(),
                                                                                                formula: group.formula,
                                                                                                position: group.conditions[0].position,
                                                                                                priceToAdd: '0',
                                                                                                quantity: String(maxQty + 10)
                                                                                            };
                                                                                            updateRuleField(rule.ruleID, 'conditions', [
                                                                                                ...rule.conditions,
                                                                                                newTier
                                                                                            ]);
                                                                                        }}
                                                                                    >
                                                                                        + {T._('Add tier', 'Admin')}
                                                                                    </AddTextButton>
                                                                                </QuantityTierContainer>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}

                                                            <AddTextButton
                                                                type='button'
                                                                onClick={() => {
                                                                    const newId = Date.now();
                                                                    const newCond: PricingConditionData = {
                                                                        conditionID: newId,
                                                                        formula: buildFormula('=', ['']),
                                                                        position: rule.conditions.length,
                                                                        priceToAdd: '0',
                                                                        quantity: '0'
                                                                    };
                                                                    updateRuleField(rule.ruleID, 'conditions', [...rule.conditions, newCond]);
                                                                }}
                                                            >
                                                                + {T._('Add condition', 'Admin')}
                                                            </AddTextButton>

                                                            {/* Targets summary */}
                                                            {rule.targets.length > 0 && (
                                                                <TargetsSection>
                                                                    <FormLabel>{T._('Targets', 'Admin')}</FormLabel>
                                                                    <TargetsInfo>
                                                                        {rule.targets.map((target, targetIndex) => (
                                                                            <span key={target.targetID}>
                                                                                {[target.area, target.color, target.side].filter(Boolean).join(' / ') ||
                                                                                    T._('All', 'Admin')}
                                                                                {targetIndex < rule.targets.length - 1 && ', '}
                                                                            </span>
                                                                        ))}
                                                                        <SettingHint>{T._('Target editing in full mode', 'Admin')}</SettingHint>
                                                                    </TargetsInfo>
                                                                </TargetsSection>
                                                            )}
                                                        </PricingRuleCardBody>
                                                    )}
                                                </PricingRuleCard>
                                            );
                                        })
                                    )}

                                    {/* Add rule button */}
                                    <AddRuleButton
                                        type='button'
                                        onClick={() => {
                                            if (!pricingGroup || properties.length === 0) return;
                                            const firstProp = properties[0];
                                            const newRule: PricingRuleData = {
                                                ruleID: Date.now(),
                                                propertyID: firstProp.propertyID,
                                                propertyName: firstProp.propertyName,
                                                elementID: 0,
                                                elementTitle: null,
                                                isSetupPrice: false,
                                                allMatchType: true,
                                                priceType: 0,
                                                simplified: false,
                                                conditions: [
                                                    {
                                                        conditionID: Date.now() + 1,
                                                        formula: buildFormula('=', ['']),
                                                        position: 0,
                                                        priceToAdd: '0',
                                                        quantity: '0'
                                                    }
                                                ],
                                                targets: []
                                            };
                                            updateRuleField(newRule.ruleID, 'propertyID', firstProp.propertyID);
                                            setPricingGroup({
                                                ...pricingGroup,
                                                rules: [...pricingGroup.rules, newRule]
                                            });
                                            setExpandedRules((prev) => new Set([...prev, newRule.ruleID]));
                                            setDirty(true);
                                        }}
                                    >
                                        + {T._('Add rule', 'Admin')}
                                    </AddRuleButton>
                                </SectionCard>
                            </>
                        )}
                    </StackedBody>
                </>
            )}
        </Drawer>
    );
};

export const PricingRuleEditor: FC<PricingRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer isOpen={true} isStacked onClose={props.onBack} header={{ title: '', subtitle: T._('Pricing rules', 'Admin') }} width={620}>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>&nbsp;</span> / {T._('Pricing rules', 'Admin')}
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='120'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='4' ry='4' width='40%' height='14' />
                            <rect x='0' y='28' rx='4' ry='4' width='100%' height='10' />
                            <rect x='0' y='48' rx='4' ry='4' width='80%' height='10' />
                            <rect x='0' y='72' rx='4' ry='4' width='60%' height='28' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <PricingRuleEditorContent {...props} />
        </Suspense>
    );
};
